import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  REQUEST_STATUSES,
  TYPE_COLORS,
  REQUEST_TYPE_LABELS,
  STATUS_COLORS,
} from '../../constants';
import {
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
} from '@mui/icons-material';
import { TimeOffRequest } from '../../types';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { calculateWorkingDays, isRequestInYear } from '../../utils/dateUtils';
import { formatDate } from '../../lib/formatDate';
import YearSelect from '../../components/YearSelect';

// Status labels for display
const STATUS_LABELS = {
  [REQUEST_STATUSES.PENDING]: 'Pending',
  [REQUEST_STATUSES.APPROVED]: 'Approved',
  [REQUEST_STATUSES.DENIED]: 'Denied',
} as const;

interface TimeOffRequestsListProps {
  requests: TimeOffRequest[];
  loading: boolean;
  onDeleteRequest?: (requestId: string) => void;
  onApproveRequest?: (requestId: string) => void;
  onDenyRequest?: (requestId: string) => void;
  isAdmin?: boolean;
  showActions?: boolean;
  showYearFilter?: boolean;
  holidays?: any[];
}

const TimeOffRequestsList: React.FC<TimeOffRequestsListProps> = ({
  requests,
  loading,
  onDeleteRequest,
  onApproveRequest,
  onDenyRequest,
  isAdmin = false,
  showActions = false,
  showYearFilter = false,
  holidays = [],
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TimeOffRequest | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const filteredRequests = showYearFilter
    ? requests.filter((request) => isRequestInYear(request.startDate, selectedYear))
    : requests;

  const handleDeleteClick = (request: TimeOffRequest) => {
    setSelectedRequest(request);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRequest && onDeleteRequest) {
      onDeleteRequest(selectedRequest.id);
    }
    setDeleteDialogOpen(false);
    setSelectedRequest(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRequest(null);
  };

  const calculateDays = (startDate: string, endDate: string) => {
    return calculateWorkingDays(startDate, endDate, holidays);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Loading requests...
          </Typography>
          {Array.from({ length: 5 }).map((_, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" height={60} />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!loading && filteredRequests.length === 0) {
    return (
      <Card>
        <CardContent>
          {showYearFilter && (
            <Box sx={{ mb: 2, maxWidth: 160 }}>
              <YearSelect value={selectedYear} onChange={setSelectedYear} fullWidth />
            </Box>
          )}
          <Alert severity="info">
            {showYearFilter
              ? `No time-off requests found for ${selectedYear}.`
              : 'No time-off requests found.'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">
            {showActions ? 'Pending Requests' : 'Time Off Requests'}
          </Typography>
          {showYearFilter && (
            <YearSelect value={selectedYear} onChange={setSelectedYear} />
          )}
        </Box>
        
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && <TableCell>Employee</TableCell>}
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Days</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Created</TableCell>
                {isAdmin && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id} hover>
                  {isAdmin && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {request.employee?.firstName} {request.employee?.lastName}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={REQUEST_TYPE_LABELS[request.type]}
                      color={TYPE_COLORS[request.type] as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[request.status] || request.status}
                      color={STATUS_COLORS[request.status] as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(request.startDate)}
                  </TableCell>
                  <TableCell>
                    {formatDate(request.endDate)}
                  </TableCell>
                  <TableCell>
                    {calculateDays(request.startDate, request.endDate)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      maxWidth: 200,
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap'
                    }}>
                      {request.reason || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {formatDate(request.createdAt)}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {showActions && isAdmin && request.status === REQUEST_STATUSES.PENDING && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => onApproveRequest?.(request.id)}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deny">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDenyRequest?.(request.id)}
                              >
                                <DenyIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        
                        {!showActions && onDeleteRequest && isAdmin && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(request)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Request"
        itemName={selectedRequest ? `${selectedRequest.employee?.firstName} ${selectedRequest.employee?.lastName}` : ''}
        itemType="time-off request"
      />
    </Card>
  );
};

export default TimeOffRequestsList; 